/**
 * HR API — personnel, candidates, assignments
 * @see services/hrApi.js barrel export
 */

import { api } from '../../api';

// =============================================================================
// CANDIDATES
// =============================================================================

export const getCandidate = async (candidateId) => {
  const response = await api.get(`/candidates/${candidateId}`);
  return response.data;
};

export const getCandidates = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) queryParams.append(key, value);
  });
  const response = await api.get(`/candidates?${queryParams.toString()}`);
  return response.data;
};

/**
 * Approve candidate and create company employee (Task 13.2)
 * 
 * Creates a company-level employee with work_context='COMPANY'.
 * Employee will be available for project assignment.
 * 
 * @param {number} candidateId - Candidate ID
 * @param {object} approvalData - Approval data
 * @param {string} approvalData.hire_date - YYYY-MM-DD hire date
 * @param {number} approvalData.position_id - Position/role ID
 * @param {number} [approvalData.department_id] - Optional department ID
 * @param {number} [approvalData.base_salary] - Optional base salary
 * @param {string} [approvalData.salary_currency] - Currency (default: USD)
 * @param {string} [approvalData.payment_frequency] - Frequency (default: MONTHLY)
 * @param {string} [approvalData.personnel_type] - Type (default: PROJECT_STAFF)
 * @param {string} [approvalData.notes] - Optional notes
 * @returns {Promise} Employee creation result with employee_code
 */
export const approveCandidate = async (candidateId, approvalData) => {
  try {
    const response = await api.post(
      `/candidates/${candidateId}/approve`,
      approvalData
    );
    return response.data;
  } catch (error) {
    console.error('Error approving candidate:', error);
    throw error;
  }
};

// =============================================================================
// PERSONNEL ROLES & DEPARTMENTS
// =============================================================================

export const getRoles = async () => {
  const response = await api.get('/personnel-roles');
  return response.data;
};

export const getDepartments = async () => {
  const response = await api.get('/personnel-departments');
  return response.data;
};

// =============================================================================
// PROJECT PERSONNEL
// =============================================================================

/**
 * @param {Object} [params] - Passed to GET /personnel (e.g. project_id, status, include_terminated).
 *   Optional roster_year + roster_month (both together) scope the list to who appeared on the project in that calendar month.
 */
export const getProjectPersonnel = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (key === 'include_terminated') {
      if (value === true) queryParams.append('include_terminated', 'true');
      return;
    }
    if (value === undefined || value === null || value === '') return;
    // Map project_id to current_project_id for the backend
    const paramKey = key === 'project_id' ? 'current_project_id' : key;
    queryParams.append(paramKey, String(value));
  });
  const response = await api.get(`/personnel?${queryParams.toString()}`);
  return response.data;
};

export const getPersonnel = async (personnelId) => {
  const response = await api.get(`/personnel/${personnelId}`);
  return response.data;
};

export const createPersonnel = async (personnelData) => {
  const response = await api.post('/personnel', personnelData);
  return response.data;
};

export const updatePersonnel = async (personnelId, personnelData) => {
  const response = await api.put(`/personnel/${personnelId}`, personnelData);
  return response.data;
};

export const terminatePersonnel = async (personnelId, endDate, reason, notes) => {
  const response = await api.post(`/hr/personnel/${personnelId}/terminate`, null, {
    params: { end_date: endDate, reason, notes }
  });
  return response.data;
};

export const getNextEmployeeCode = async (projectId) => {
  const response = await api.get(`/hr/personnel/next-code/${projectId}`);
  return response.data;
};

export const getPersonnelByCandidate = async (candidateId) => {
  const response = await api.get(`/hr/personnel/by-candidate/${candidateId}`);
  return response.data;
};

export const hireFromCandidate = async (data) => {
  // data: { candidate_id, project_id, position_id, secondary_position_id, department_id, start_date, notes }
  const response = await api.post('/hr/personnel/hire-from-candidate', data);
  return response.data;
};

export const reactivatePersonnel = async (personnelId, startDate, notes) => {
  const response = await api.post(`/hr/personnel/${personnelId}/reactivate`, null, {
    params: { start_date: startDate, notes }
  });
  return response.data;
};

// =============================================================================
// UNIFIED PERSONNEL API - Uses new /api/v1/personnel endpoints (FASE 2)
// =============================================================================

/**
 * Get list of personnel with filters and pagination (UNIFIED ENDPOINT)
 * @param {Object} params - Query parameters
 * @param {number} params.skip - Number of records to skip (pagination)
 * @param {number} params.limit - Number of records to return
 * @param {string} params.work_context - Filter by context: 'COMPANY', 'PROJECT', or '' for all
 * @param {string} params.status - Filter by employment status (ACTIVE, ON_LEAVE, TERMINATED)
 * @param {string} params.personnel_type - Filter by type (PERMANENT_STAFF, PROJECT_STAFF, OVERHEAD, CONTRACTOR)
 * @param {number} params.department_id - Filter by department ID
 * @param {boolean} params.available_only - Only show personnel available for assignment
 * @param {string} params.search - Search by name, employee code, or document ID
 * @param {number} [params.roster_year] - With roster_month, filter roster to that calendar month (must send both)
 * @param {number} [params.roster_month] - 1–12; with roster_year, filter roster to that calendar month (must send both)
 * @returns {Promise} Array of personnel records
 */
export const getUnifiedPersonnel = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  const response = await api.get(`/personnel?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get single personnel by ID (UNIFIED ENDPOINT)
 * @param {number} personnelId - Personnel ID
 * @returns {Promise} Personnel with all details
 */
export const getUnifiedPersonnelById = async (personnelId) => {
  const response = await api.get(`/personnel/${personnelId}`);
  return response.data;
};

/**
 * Create new personnel record (UNIFIED ENDPOINT)
 * Employee code is auto-generated in format EMP-YYYY-NNNN
 * @param {Object} data - Personnel data
 * @returns {Promise} Created personnel record with employee_code
 */
export const createUnifiedPersonnel = async (data) => {
  const response = await api.post('/personnel', data);
  return response.data;
};

/**
 * Update personnel information (UNIFIED ENDPOINT)
 * Protected fields: employee_code, work_context, current_project_id
 * @param {number} personnelId - Personnel ID
 * @param {Object} data - Updated fields
 * @returns {Promise} Updated personnel record
 */
export const updateUnifiedPersonnel = async (personnelId, data) => {
  const response = await api.put(`/personnel/${personnelId}`, data);
  return response.data;
};

/**
 * Delete personnel record (UNIFIED ENDPOINT)
 * @param {number} personnelId - Personnel ID
 * @param {boolean} hardDelete - If true, permanently delete; if false (default), soft delete
 * @returns {Promise} Response
 */
export const deleteUnifiedPersonnel = async (personnelId, hardDelete = false) => {
  const response = await api.delete(`/personnel/${personnelId}?hard_delete=${hardDelete}`);
  return response.data;
};

/**
 * Assign personnel to project - transitions COMPANY → PROJECT context
 * @param {number} personnelId - Personnel ID
 * @param {Object} data - Assignment details
 * @param {number} data.project_id - Project ID
 * @param {string} data.start_date - Assignment start date (YYYY-MM-DD)
 * @param {number} data.daily_rate - Daily rate for this project
 * @param {number} data.position_id - Optional position ID for project
 * @param {string} data.end_date - Optional expected end date
 * @param {string} data.notes - Optional notes
 * @returns {Promise} Updated personnel record with new context
 */
export const assignToProject = async (personnelId, data) => {
  const response = await api.post(`/personnel/${personnelId}/assign-to-project`, data);
  return response.data;
};

/**
 * Return personnel to company - transitions PROJECT → COMPANY context
 * @param {number} personnelId - Personnel ID
 * @param {Object} data - Return details
 * @param {string} data.return_date - Return date (YYYY-MM-DD)
 * @param {string} data.reason - Optional reason for return
 * @param {string} data.notes - Optional notes
 * @returns {Promise} Updated personnel record with COMPANY context
 */
export const returnToCompany = async (personnelId, data) => {
  const response = await api.post(`/personnel/${personnelId}/return-to-company`, data);
  return response.data;
};

/**
 * Terminate personnel employment (UNIFIED ENDPOINT)
 * POST body matches backend PersonnelTermination: termination_reason, termination_notes, blacklist.
 *
 * @param {number} personnelId - Personnel ID (project_personnel.id)
 * @param {Object} data - Termination details (accepts UI aliases for backward compatibility)
 * @param {string} data.termination_date - Termination date (YYYY-MM-DD)
 * @param {string} [data.termination_reason] - Reason (preferred)
 * @param {string} [data.reason] - Alias for termination_reason (modals / CompanyPersonnelDetail)
 * @param {string|null} [data.termination_notes] - Optional notes
 * @param {string|null} [data.notes] - Alias for termination_notes
 * @param {boolean} [data.blacklist] - Add to blacklist
 * @param {boolean} [data.add_to_blacklist] - Alias for blacklist
 * @param {string|null} [data.blacklist_reason] - Required by API when blacklisting
 * @returns {Promise} Updated personnel record
 */
export const terminateUnifiedPersonnel = async (personnelId, data) => {
  const termination_reason = data.termination_reason ?? data.reason;
  const rawNotes = data.termination_notes ?? data.notes;
  const termination_notes =
    rawNotes != null && String(rawNotes).trim() !== '' ? String(rawNotes).trim() : null;
  const blacklist = Boolean(data.blacklist ?? data.add_to_blacklist);
  const blacklist_reason =
    blacklist &&
    data.blacklist_reason != null &&
    String(data.blacklist_reason).trim() !== ''
      ? String(data.blacklist_reason).trim()
      : null;

  const body = {
    termination_date: data.termination_date,
    termination_reason,
    termination_notes,
    blacklist,
    blacklist_reason,
  };

  const response = await api.post(`/personnel/${personnelId}/terminate`, body);
  return response.data;
};

/**
 * Reactivate terminated personnel (UNIFIED ENDPOINT)
 * @param {number} personnelId - Personnel ID
 * @param {Object} data - Reactivation details
 * @param {string} data.reactivation_date - Reactivation date (YYYY-MM-DD)
 * @param {string} data.notes - Optional notes
 * @returns {Promise} Updated personnel record with ACTIVE status
 */
export const reactivateUnifiedPersonnel = async (personnelId, data) => {
  const response = await api.post(`/personnel/${personnelId}/reactivate`, data);
  return response.data;
};

/**
 * Get personnel available for project assignment (UNIFIED ENDPOINT)
 * @param {Object} params - Filter parameters
 * @param {number} params.position_id - Filter by position
 * @param {number} params.department_id - Filter by department
 * @returns {Promise} List of available personnel
 */
export const getAvailableForAssignment = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  const response = await api.get(`/personnel/available?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get personnel statistics summary (UNIFIED ENDPOINT)
 * @returns {Promise} Statistics with counts by context, status, type
 */
export const getUnifiedPersonnelStats = async () => {
  const response = await api.get('/personnel/statistics/summary');
  return response.data;
};

/**
 * Get assignment history for specific personnel (UNIFIED ENDPOINT)
 * @param {number} personnelId - Personnel ID
 * @returns {Promise} Array of assignment history records
 */
export const getUnifiedAssignmentHistory = async (personnelId) => {
  const response = await api.get(`/personnel/${personnelId}/assignment-history`);
  return response.data;
};

/**
 * Get assignment history filtered by date range
 * @param {number} personnelId - Personnel ID
 * @param {string} startDate - Filter start date (YYYY-MM-DD)
 * @param {string} endDate - Filter end date (YYYY-MM-DD)
 * @returns {Promise} Array of assignment history records
 */
export const getAssignmentHistoryByDateRange = async (personnelId, startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const response = await api.get(`/personnel/${personnelId}/assignment-history/date-range?${params.toString()}`);
  return response.data;
};

// =============================================================================
// LEGACY COMPANY PERSONNEL - Kept for backward compatibility
// =============================================================================

/**
 * @deprecated Use getUnifiedPersonnel instead
 */
export const getCompanyPersonnel = async (params = {}) => {
  return getUnifiedPersonnel(params);
};

/**
 * @deprecated Use getUnifiedPersonnelById instead
 */
export const getCompanyPersonnelById = async (personnelId) => {
  return getUnifiedPersonnelById(personnelId);
};

/**
 * @deprecated Use createUnifiedPersonnel instead
 */
export const createCompanyPersonnel = async (data) => {
  return createUnifiedPersonnel(data);
};

/**
 * @deprecated Use updateUnifiedPersonnel instead
 */
export const updateCompanyPersonnel = async (personnelId, data) => {
  return updateUnifiedPersonnel(personnelId, data);
};

/**
 * @deprecated Use deleteUnifiedPersonnel instead
 */
export const deleteCompanyPersonnel = async (personnelId, hardDelete = false) => {
  return deleteUnifiedPersonnel(personnelId, hardDelete);
};

/**
 * @deprecated Use terminateUnifiedPersonnel instead
 */
export const terminateCompanyPersonnel = async (personnelId, data) => {
  return terminateUnifiedPersonnel(personnelId, data);
};

/**
 * @deprecated Use reactivateUnifiedPersonnel instead
 */
export const reactivateCompanyPersonnel = async (personnelId, data) => {
  return reactivateUnifiedPersonnel(personnelId, data);
};

/**
 * @deprecated Use getAvailableForAssignment instead
 */
export const getAvailablePersonnel = async (params = {}) => {
  return getAvailableForAssignment(params);
};

/**
 * @deprecated Use getUnifiedPersonnelStats instead
 */
export const getCompanyPersonnelStats = async () => {
  return getUnifiedPersonnelStats();
};

// =============================================================================
// PROJECT ASSIGNMENTS - Assign personnel to projects
// =============================================================================

/**
 * Assign company personnel to a project
 * @param {number} personnelId - Company personnel ID
 * @param {Object} data - Assignment details
 * @param {number} data.company_personnel_id - Company personnel ID (redundant but required by schema)
 * @param {number} data.project_id - Project ID
 * @param {string} data.assignment_start_date - Start date (YYYY-MM-DD)
 * @param {number} data.project_position_id - Position ID for this project
 * @param {number} data.project_daily_rate - Daily rate for this project
 * @param {string} data.notes - Optional notes
 * @returns {Promise} Created project assignment
 */
export const assignPersonnelToProject = async (personnelId, data) => {
  const response = await api.post(`/company-personnel/${personnelId}/assign-to-project`, data);
  return response.data;
};

/**
 * Complete/close a project assignment
 * @param {number} assignmentId - Project assignment ID
 * @param {Object} params - Completion details
 * @param {string} params.completion_date - Completion date (YYYY-MM-DD)
 * @param {string} params.notes - Optional notes
 * @returns {Promise} Updated assignment
 */
export const completeProjectAssignment = async (assignmentId, params) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  const response = await api.put(`/company-personnel/assignments/${assignmentId}/complete?${queryParams.toString()}`);
  return response.data;
};

/**
 * Transfer personnel to a different project
 * @param {number} assignmentId - Current assignment ID
 * @param {Object} data - Transfer details
 * @param {number} data.new_project_id - New project ID
 * @param {string} data.transfer_date - Transfer date (YYYY-MM-DD)
 * @param {number} data.new_position_id - Optional new position
 * @param {number} data.new_daily_rate - Optional new daily rate
 * @param {string} data.notes - Optional notes
 * @returns {Promise} New assignment record
 */
export const transferPersonnelToProject = async (assignmentId, data) => {
  const queryParams = new URLSearchParams();
  queryParams.append('new_project_id', data.new_project_id);
  queryParams.append('transfer_date', data.transfer_date);
  if (data.new_position_id) queryParams.append('new_position_id', data.new_position_id);
  if (data.new_daily_rate) queryParams.append('new_daily_rate', data.new_daily_rate);
  if (data.notes) queryParams.append('notes', data.notes);
  
  const response = await api.post(`/company-personnel/assignments/${assignmentId}/transfer?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get assignment history for specific personnel
 * @param {number} personnelId - Company personnel ID
 * @param {boolean} includeCompleted - Include completed assignments (default: true)
 * @returns {Promise} List of assignments
 */
export const getPersonnelAssignments = async (personnelId, includeCompleted = true) => {
  const response = await api.get(`/company-personnel/${personnelId}/assignments?include_completed=${includeCompleted}`);
  return response.data;
};

/**
 * Get all active project assignments across company
 * @param {Object} params - Filter parameters
 * @param {number} params.project_id - Filter by project
 * @param {number} params.department_id - Filter by department
 * @returns {Promise} List of active assignments
 */
export const getActiveAssignments = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  const response = await api.get(`/company-personnel/assignments/active?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get personnel assigned to a specific project
 * @param {number} projectId - Project ID
 * @returns {Promise} List of assigned company personnel with assignment details
 */
export const getProjectAssignedPersonnel = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/assigned-personnel`);
  return response.data;
};

// =============================================================================
// PROJECT TEAM FUNCTIONS (Task 13.3)
// =============================================================================

/**
 * Get project team members (personnel with work_context='PROJECT' in specific project)
 * 
 * @param {number} projectId - Project ID
 * @param {Object} params - Additional filter parameters
 * @returns {Promise} Personnel in project
 */
export const getProjectTeamMembers = async (projectId, params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('current_project_id', projectId);
    queryParams.append('work_context', 'PROJECT');
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    
    const response = await api.get(`/personnel?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project team members:', error);
    throw error;
  }
};

/**
 * Get project team statistics
 * 
 * @param {number} projectId - Project ID
 * @returns {Promise} Statistics object { total, active, totalDailyRate }
 */
export const getProjectTeamStats = async (projectId) => {
  try {
    const personnel = await getProjectTeamMembers(projectId, { status: 'ACTIVE' });
    
    const active = personnel.filter(p => p.status === 'ACTIVE').length;
    const totalDailyRate = personnel.reduce(
      (sum, p) => sum + (p.project_daily_rate || 0),
      0
    );
    
    return {
      total: personnel.length,
      active,
      totalDailyRate,
      personnel
    };
  } catch (error) {
    console.error('Error fetching project team stats:', error);
    throw error;
  }
};

// =============================================================================
// PERSONNEL LOCATION HISTORY
// =============================================================================

/**
 * Get full location history for a personnel member
 * @param {number} personnelId - Personnel ID
 * @returns {Promise<Array>} Location history records
 */
export const getLocationHistory = async (personnelId) => {
  try {
    const response = await api.get(`/personnel/${personnelId}/locations`);
    return response.data;
  } catch (error) {
    console.error('Error fetching location history:', error);
    throw error;
  }
};

/**
 * Get current location for a personnel member
 * @param {number} personnelId - Personnel ID
 * @returns {Promise<Object>} Current location info
 */
export const getCurrentLocation = async (personnelId) => {
  try {
    const response = await api.get(`/personnel/${personnelId}/location/current`);
    return response.data;
  } catch (error) {
    console.error('Error fetching current location:', error);
    throw error;
  }
};

/**
 * Add a new location assignment for personnel
 * @param {number} personnelId - Personnel ID
 * @param {Object} locationData - Location data
 * @param {string} locationData.location_name - Short name (required)
 * @param {string} locationData.location_long_name - Long name (optional)
 * @param {number} locationData.location_category_id - QHSE category ID (optional)
 * @param {string} locationData.start_date - Start date YYYY-MM-DD (required)
 * @param {string} locationData.transfer_reason - Reason for transfer (optional)
 * @param {string} locationData.notes - Additional notes (optional)
 * @returns {Promise<Object>} Created location record
 */
export const addLocationAssignment = async (personnelId, locationData) => {
  try {
    const response = await api.post(`/personnel/${personnelId}/locations`, locationData);
    return response.data;
  } catch (error) {
    console.error('Error adding location assignment:', error);
    throw error;
  }
};

